import React, { useEffect } from 'react'
import './index.css'
import {
  BrowserRouter,
  Routes,
  Route,
  useParams,
  Navigate,
} from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Spinner from './components/Spinner'
import { useDispatch } from 'react-redux'
import { getCookie } from './utils/cookieUtils'
import { signIn, setBag } from './api/features/authSlice'
import { updateCart } from './api/features/cartSlice'
import { useGetCurrentUserQuery } from './api/services/usersApi'
import { CountryProvider, useGlobalCountry, useCountry } from './context/CountryContext'
import CountrySelectionModal from './components/CountrySelectionModal'
import { VALID_COUNTRIES, COUNTRY_CONFIG } from './context/CountryContext'
import { I18nProvider } from './i18n/I18nProvider'
import CookieBanner from './components/CookieBanner'

const Loyalty = React.lazy(() => import('./pages/adminPages/Loyalty'))
const Layout = React.lazy(() => import('./components/Layout'))
const HomePage = React.lazy(() => import('./pages/HomePage'))
const AdminPanel = React.lazy(() => import('./pages/adminPages/AdminPanel'))
const Checkout = React.lazy(() => import('./pages/Checkout'))
const Selective = React.lazy(() => import('./pages/Selective'))
const AboutParfum = React.lazy(() => import('./pages/AboutParfum'))
const Korpa = React.lazy(() => import('./pages/Korpa'))
const Favorites = React.lazy(() => import('./pages/Favorites'))
const ScrollToTop = React.lazy(() => import('./components/ScrollToTop'))
const Login = React.lazy(() => import('./pages/Login'))
const Error = React.lazy(() => import('./pages/Error'))
const AdminDashLayout = React.lazy(() =>
  import('./components/Layouts/Admin/AdminDashLayout'),
)
const CheckoutComplete = React.lazy(() => import('./pages/checkoutComplete'))
const AdminDashboard = React.lazy(() =>
  import('./pages/adminPages/AdminDashboard'),
)
const ScentCrud = React.lazy(() => import('./pages/adminPages/ScentCrud'))
const ProtectedLogin = React.lazy(() =>
  import('./components/protectedRoute/ProtectedLogin'),
)
const ArticleCrud = React.lazy(() => import('./pages/adminPages/ArticleCrud'))
const UpdateArticle = React.lazy(() =>
  import('./components/ArticleCrud/UpdateArticle'),
)
const Customers = React.lazy(() => import('./pages/adminPages/Customers'))
const Orders = React.lazy(() => import('./pages/adminPages/Orders'))
const Messages = React.lazy(() => import('./pages/adminPages/Messages'))
const ProtectedAdmin = React.lazy(() =>
  import('./components/protectedRoute/ProtectedAdmin'),
)
const Settings = React.lazy(() => import('./pages/adminPages/Settings'))
const UserLayout = React.lazy(() =>
  import('./components/Layouts/User/UserLayout'),
)
const UserProfile = React.lazy(() => import('./pages/userPages/UserProfile'))
const NotificationOptions = React.lazy(() =>
  import('./pages/userPages/NotificationOptions'),
)
const UserPayment = React.lazy(() => import('./pages/userPages/UserPayment'))
const UserPrivacy = React.lazy(() => import('./pages/userPages/UserPrivacy'))
const UserOrders = React.lazy(() => import('./pages/userPages/UserOrders'))
const ProtectedCustomer = React.lazy(() =>
  import('./components/protectedRoute/ProtectedCustomer'),
)
const Volumes = React.lazy(() => import('./pages/adminPages/Volumes'))
const Coupon = React.lazy(() => import('./pages/adminPages/Coupon'))
const Discount = React.lazy(() => import('./pages/adminPages/Discount'))
const Verification = React.lazy(() => import('./pages/Verification'))
const AllArticleShop = React.lazy(() =>
  import('./components/homePage/AllArticleShop'),
)
const SaleArticles = React.lazy(() => import('./pages/SaleArticles'))
const ForgotPasswordEmail = React.lazy(() =>
  import('./components/login/ForgotPasswordEmail'),
)
const ChangePassword = React.lazy(() =>
  import('./components/login/ChangePassword'),
)
const Register = React.lazy(() => import('./components/login/Register'))
const Kontakt = React.lazy(() => import('./pages/Kontakt'))
const Onama = React.lazy(() => import('./pages/Onama'))
const Dostava = React.lazy(() => import('./pages/Dostava'))
const UsloviKoristenja = React.lazy(() => import('./pages/UsloviKoristenja'))
const PravilaPrivatnosti = React.lazy(() =>
  import('./pages/PravilaPrivatnosti'),
)
const PolitikaKolacica = React.lazy(() => import('./pages/PolitikaKolacica'))
const Maintenance = React.lazy(() => import('./pages/Maintenance'))
const MaintenanceGuard = React.lazy(() =>
  import('./components/protectedRoute/MaintenanceGuard'),
)

function CountryValidator({ children }) {
  const { countryCode } = useParams()

  if (!VALID_COUNTRIES.includes(countryCode)) {
    return <Navigate to="/ba" replace />
  }

  return children
}

function RootRedirect() {
  const savedCountry = getCookie('selectedCountry')
  const defaultCountry =
    savedCountry && VALID_COUNTRIES.includes(savedCountry)
      ? savedCountry
      : 'ba'
  return <Navigate to={`/${defaultCountry}`} replace />
}

// Global Country Modal Component (outside of country-specific routes)
function GlobalCountryModal() {
  const { country, selectCountry, hasCountry } = useGlobalCountry()
  const [showModal, setShowModal] = React.useState(false)

  useEffect(() => {
    // Show modal if no country is selected and we're not on special routes
    const currentPath = window.location.pathname
    const isSpecialRoute =
      currentPath.startsWith('/admin') ||
      currentPath.startsWith('/login') ||
      currentPath.startsWith('/korisnik') ||
      currentPath.startsWith('/verification') ||
      currentPath.startsWith('/password-change') ||
      currentPath.startsWith('/odrzavanje')

    if (!hasCountry && !isSpecialRoute) {
      setShowModal(true)
    }
  }, [hasCountry])

  const handleCountrySelect = (countryCode) => {
    selectCountry(countryCode)
    setShowModal(false)

    // Navigate to the selected country
    const currentPath = window.location.pathname
    let newPath

    if (currentPath === '/' || currentPath === '') {
      newPath = `/${countryCode}`
    } else {
      // Clean path of any existing country codes
      let cleanPath = currentPath
      VALID_COUNTRIES.forEach((country) => {
        if (cleanPath.startsWith(`/${country}`)) {
          cleanPath = cleanPath.replace(`/${country}`, '')
        }
      })
      newPath = `/${countryCode}${cleanPath || ''}`
    }

    window.location.href = newPath // Force navigation
  }

  return (
    <CountrySelectionModal
      isOpen={showModal}
      onSelectCountry={handleCountrySelect}
      countries={VALID_COUNTRIES}
      countryConfigs={COUNTRY_CONFIG}
    />
  )
}

// Layout wrapper that includes country context and modal
function CountryAwareLayout() {
  const { currentCountry } = useCountry()
  return (
    <I18nProvider countryCode={currentCountry}>
      <Layout />
    </I18nProvider>
  )
}

function CountryAwareLayoutWrapper() {
  return (
    <CountryProvider>
      <CountryAwareLayout />
    </CountryProvider>
  )
}

// Main app content with routing
function AppContent() {
  const { data, isSuccess } = useGetCurrentUserQuery()
  const dispatch = useDispatch()

  // Initialize Redux state from cookies when app loads
  useEffect(() => {
    const bag = getCookie('bag', true)
    const cart = getCookie('cart', true)

    if (isSuccess && data?.user) {
      dispatch(signIn(data?.user))
    }

    if (bag) {
      dispatch(setBag(bag))
    }

    if (cart) {
      dispatch(updateCart(cart || []))
    }
  }, [dispatch, data, isSuccess])

  return (
    <>
      <ScrollToTop />
      <React.Suspense
        fallback={
          <div className="flex items-center justify-center h-screen">
            <Spinner />
          </div>
        }
      >
        <Routes>
          <Route path="/odrzavanje" element={<Maintenance />} />

          <Route element={<ProtectedAdmin />}>
            <Route
              path="/admin"
              element={
                <CountryProvider>
                  <AdminDashLayout />
                </CountryProvider>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="articles" element={<ArticleCrud />} />
              <Route path="add_article" element={<AdminPanel />} />
              <Route path="scentcrud" element={<ScentCrud />} />
              <Route path="customers" element={<Customers />} />
              <Route path="loyalty" element={<Loyalty />} />
              <Route path="orders" element={<Orders />} />
              <Route path="messages" element={<Messages />} />
              <Route path="settings" element={<Settings />} />
              <Route path="volumes" element={<Volumes />} />
              <Route path="coupon" element={<Coupon />} />
              <Route path="discount" element={<Discount />} />
              <Route
                path="update_article/:article_id"
                element={<UpdateArticle />}
              />
            </Route>
          </Route>

          <Route path="/login" element={<ProtectedLogin />}>
            <Route index element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPasswordEmail />} />
          </Route>
          <Route path="/verification" element={<Verification />} />
          <Route path="/password-change" element={<ChangePassword />} />

          <Route element={<MaintenanceGuard />}>
            <Route path="/" element={<RootRedirect />} />
            <Route
              path="/:countryCode"
              element={
                <CountryValidator>
                  <CountryAwareLayoutWrapper />
                </CountryValidator>
              }
            >
              <Route index element={<HomePage />} />
              <Route path="checkout" element={<Checkout />} />
              <Route path="favorites" element={<Favorites />} />
              <Route path="shop" element={<AllArticleShop />} />
              <Route path="akcija" element={<SaleArticles />} />
              <Route path=":parfume_type" element={<Selective />} />
              <Route path="article/:id" element={<AboutParfum />} />
              <Route path="korpa" element={<Korpa />} />
              <Route path="checkout_complete" element={<CheckoutComplete />} />
              <Route path="kontakt" element={<Kontakt />} />
              <Route
                path="politikaprivatnosti"
                element={<PravilaPrivatnosti />}
              />
              <Route path="onama" element={<Onama />} />
              <Route path="dostava" element={<Dostava />} />
              <Route path="uslovikoristenja" element={<UsloviKoristenja />} />
              <Route path="politikakolacica" element={<PolitikaKolacica />} />
              <Route
                path="pravilaprivatnosti"
                element={<PravilaPrivatnosti />}
              />
            </Route>

            <Route element={<ProtectedCustomer />}>
              <Route path="/korisnik" element={<UserLayout />}>
                <Route index element={<UserProfile />} />
                <Route path="notifications" element={<NotificationOptions />} />
                <Route path="user_payment" element={<UserPayment />} />
                <Route path="user_privacy" element={<UserPrivacy />} />
                <Route path="orders" element={<UserOrders />} />
              </Route>
            </Route>

            <Route path="*" element={<Error />} />
          </Route>
        </Routes>
      </React.Suspense>

      {/* Global Country Selection Modal */}
      <GlobalCountryModal />

      <CookieBanner />

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </>
  )
}

// Main App component
function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AppContent />
      </I18nProvider>
    </BrowserRouter>
  )
}

export default App
